var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { initial, success } from '@devexperts/remote-data-ts';
import { findFirst, flatten, uniq } from 'fp-ts/lib/Array';
import { fromNullable, getSetoid, none } from 'fp-ts/lib/Option';
import { asks } from 'fp-ts/lib/Reader';
import { setoidNumber } from 'fp-ts/lib/Setoid';
import { combineLatest, from, merge, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, scan, share, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { dataUrlToBlob, readFiles, toRemoteData } from '../utils/axios';
import { switchMapRD, tapRD } from '../utils/object.utils';
function mockTeam(team) {
    return __assign(__assign({}, team), { 
        /*owner: {
            email: 'kokovtsev@gmail.com',
            name: 'Dmitry K1',
            id: 3,
            profile: { avatar: undefined },
        },*/
        members: team.members ||
            [] /*[
        {
            email: 'kokovtsev+2@gmail.com',
            name: 'Dmitry K2',
            id: 2,
            profile: { avatar: undefined },
        },
    ]*/, prospects: team.prospects ||
            [] /*[
        {
            email: 'kokovtsev+3@gmail.com',
            name: 'Dmitry K3',
            id: 4,
            pending_invite: true,
            profile: { avatar: undefined },
        },
    ]*/ });
}
export var teamsService = asks(function (ctx) {
    var actions$ = new Subject();
    var getTeams = function () { return toRemoteData(ctx.axios.get('/api/teams')); };
    var getTeamDetails = function (id) {
        return toRemoteData(ctx.axios.get("/api/teams/" + id)).pipe(tapRD(function (team) { return actions$.next({ type: 'update', team: team }); }));
    };
    var initialDataAction$ = ctx.authService.userId$.pipe(distinctUntilChanged(getSetoid(setoidNumber).equals), switchMap(function (userId) {
        return userId.fold(of(success([])), getTeams).pipe(map(function (data) { return ({ type: 'data', data: data }); }));
    }));
    var createTeam = function (name) {
        return toRemoteData(ctx.axios.post("/api/teams", { name: name })).pipe(tapRD(function (team) { return actions$.next({ type: 'add', team: team }); }), share());
    };
    var updateTeam = function (teamId, update) {
        var data = new FormData();
        data.append('name', update.name);
        var avatar$ = update.avatar ? from(readFiles([update.avatar])) : of([]);
        return avatar$.pipe(switchMap(function (files) {
            if (files.length) {
                data.append('avatar', dataUrlToBlob(files[0].data), files[0].name);
            }
            return toRemoteData(ctx.axios.patch("/api/teams/" + teamId, data, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })).pipe(tapRD(function (team) { return actions$.next({ type: 'update', team: team }); }));
        }));
    };
    var inviteMembers = function (teamId, emails) {
        return toRemoteData(ctx.axios.post("/api/teams/" + teamId + "/share", { emails: emails })).pipe(switchMapRD(function () { return getTeamDetails(teamId); }));
    };
    var resendInvite = function (teamId, prospectId) {
        return toRemoteData(ctx.axios.post("/api/teams/" + teamId + "/reshare", { prospect_id: prospectId }));
    };
    var removeMember = function (teamId, userId) {
        return toRemoteData(ctx.axios.delete("/api/teams/" + teamId + "/members/" + userId)).pipe(switchMapRD(function () { return getTeamDetails(teamId); }));
    };
    /**
     * Uses the same endpoint as `removeMember` but also removes the team from the cached list of teams
     */
    var leaveTeam = function (teamId, userId) {
        return toRemoteData(ctx.axios.delete("/api/teams/" + teamId + "/members/" + userId)).pipe(tapRD(function () { return actions$.next({ type: 'delete', teamId: teamId }); }), share());
    };
    var removeProspect = function (teamId, prospectId) {
        return toRemoteData(ctx.axios.delete("/api/teams/" + teamId + "/prospects/" + prospectId)).pipe(switchMapRD(function () { return getTeamDetails(teamId); }));
    };
    var deleteTeam = function (teamId) {
        return toRemoteData(ctx.axios.delete("/api/teams/" + teamId)).pipe(tapRD(function () { return actions$.next({ type: 'delete', teamId: teamId }); }), share());
    };
    var initialState = { teams: initial, selectedTeam: none, selectedTeamId: none };
    var state$ = merge(initialDataAction$, actions$).pipe(scan(function (state, action) {
        switch (action.type) {
            case 'data':
                var teams_1 = action.data.map(function (t) { return t.map(mockTeam); });
                var newState = __assign(__assign({}, state), { teams: teams_1 });
                if (teams_1.isSuccess()) {
                    newState.selectedTeam = newState.selectedTeamId.mapNullable(function (id) {
                        return teams_1.value.find(function (t) { return t.id === id; });
                    });
                    newState.selectedTeamId = newState.selectedTeam.map(function (t) { return t.id; });
                }
                return newState;
            case 'add':
                return __assign(__assign({}, state), { teams: state.teams.map(function (teams) { return __spreadArray(__spreadArray([], teams, true), [mockTeam(action.team)], false); }) });
            case 'update': {
                var teams_2 = state.teams.map(function (teams) {
                    return teams.some(function (t) { return t.id === action.team.id; })
                        ? teams.map(function (t) { return (t.id === action.team.id ? action.team : t); })
                        : __spreadArray(__spreadArray([], teams, true), [action.team], false);
                });
                var newState_1 = __assign(__assign({}, state), { teams: teams_2 });
                if (teams_2.isSuccess()) {
                    newState_1.selectedTeam = newState_1.selectedTeamId.mapNullable(function (id) {
                        return teams_2.value.find(function (t) { return t.id === id; });
                    });
                    newState_1.selectedTeamId = newState_1.selectedTeam.map(function (t) { return t.id; });
                }
                return newState_1;
            }
            case 'delete':
                return {
                    teams: state.teams.map(function (teams) { return teams.filter(function (t) { return t.id !== action.teamId; }); }),
                    selectedTeam: state.selectedTeam.filter(function (t) { return t.id !== action.teamId; }),
                    selectedTeamId: state.selectedTeamId.filter(function (id) { return id !== action.teamId; }),
                };
            case 'select':
                return __assign(__assign({}, state), { selectedTeamId: action.teamId, selectedTeam: action.teamId.chain(function (teamId) {
                        return state.teams
                            .toOption()
                            .fold(state.selectedTeam, function (teams) { return findFirst(teams, function (t) { return t.id === teamId; }); });
                    }) });
        }
    }, initialState), startWith(initialState), shareReplay(1));
    var selectedTeam$ = state$.pipe(map(function (state) { return state.selectedTeam; }), distinctUntilChanged(), map(function (team) { return success(team); }));
    var teams$ = state$.pipe(map(function (state) { return state.teams; }), distinctUntilChanged());
    var ownTeams$ = combineLatest(teams$, ctx.authService.userId$).pipe(map(function (_a) {
        var teams = _a[0], userId = _a[1];
        return teams.map(function (teams) { return teams.filter(function (team) { return team.owner.id === userId.getOrElse(0); }); });
    }));
    var ownTeamMembers$ = ownTeams$.pipe(map(function (teams) {
        return teams.map(function (teams) {
            return uniq(setoidNumber)(flatten(teams.map(function (team) { return team.members; })).map(function (member) { return member.id; })).length +
                uniq(setoidNumber)(flatten(teams.map(function (team) { return team.prospects; })).map(function (member) { return member.id; })).length;
        });
    }));
    return {
        getTeams: getTeams,
        createTeam: createTeam,
        updateTeam: updateTeam,
        inviteMembers: inviteMembers,
        resendInvite: resendInvite,
        removeMember: removeMember,
        leaveTeam: leaveTeam,
        removeProspect: removeProspect,
        deleteTeam: deleteTeam,
        teams$: teams$,
        selectedTeam$: selectedTeam$,
        selectTeam: function (teamId) { return actions$.next({ type: 'select', teamId: teamId }); },
        ownTeamMembers$: ownTeamMembers$,
        ownTeams$: ownTeams$,
    };
});
export function pluralTeamMembers(num) {
    switch (num) {
        case 1:
            return 'team member';
        default:
            return 'team members';
    }
}
export function pluralTeams(num) {
    switch (num) {
        case 1:
            return 'team';
        default:
            return 'teams';
    }
}
export function getProductTeam(product) {
    return fromNullable(product.team).map(function (team) { return team.id; });
}
//# sourceMappingURL=teams.service.js.map